import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ClipboardList, Loader2, FileCheck2, CheckCircle2, Clock, ShieldCheck, GraduationCap, ExternalLink, Eye, Building2, Undo2, X, PenLine, Send, History, Search, Trash2 } from "lucide-react";

import ConfirmedEditModal from "./ConfirmedEditModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: "Čaká na schválenie", cls: "text-amber-300 border-amber-400/40 bg-amber-400/5", Icon: Clock },
  confirmed: { label: "Schválené", cls: "text-[#F2E3C6] border-[#D4AF37]/40 bg-[#D4AF37]/5", Icon: CheckCircle2 },
  returned: { label: "Vrátené na opravu", cls: "text-red-300 border-red-400/40 bg-red-400/5", Icon: Undo2 },
};

export default function AdminRecords({ token, pendingOnly = false, onChange }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [prevId, setPrevId] = useState("");
  const [ret, setRet] = useState(null);
  const [retBusy, setRetBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [histQ, setHistQ] = useState("");

  const resend = async (a) => {
    const to = [a.subject_email, "mentor"].filter(Boolean).join(" + ");
    if (!window.confirm(`Poslať znova aktuálne PDF (${a.subject_name}, ${a.record_no})? Príjemcovia: ${to}.`)) return;
    setBusyId(a.id);
    try {
      await axios.post(`${API}/admin/mentor-audits/${a.id}/resend`, {}, auth);
      alert("Opravené PDF bolo odoslané.");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Odoslanie zlyhalo."); }
    finally { setBusyId(""); }
  };

  const removeRec = async (a) => {
    if (!window.confirm(`Naozaj zmazať záznam ${a.subject_name}${a.record_no ? ` (${a.record_no})` : ""}?`)) return;
    setBusyId(a.id);
    try { await axios.delete(`${API}/admin/mentor-audits/${a.id}`, auth); load(); onChange && onChange(); }
    catch (e) { alert(e?.response?.data?.detail || "Zmazanie zlyhalo."); }
    finally { setBusyId(""); }
  };

  const sendReturn = async () => {
    if (ret.note.trim().length < 3) { alert("Napíšte poznámku, čo treba opraviť."); return; }
    setRetBusy(true);
    try {
      await axios.post(`${API}/admin/mentor-audits/${ret.a.id}/return`, { note: ret.note.trim() }, auth);
      setRet(null); load(); onChange && onChange();
    } catch (e) { alert(e?.response?.data?.detail || "Vrátenie zlyhalo."); }
    finally { setRetBusy(false); }
  };

  const preview = async (id) => {
    setPrevId(id);
    try {
      const r = await axios.get(`${API}/admin/mentor-audits/${id}/preview`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert("Náhľad sa nepodarilo vytvoriť."); }
    finally { setPrevId(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mentor-audits`, auth);
      setList(data.audits || []);
    } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pendingOnly]);

  useEffect(() => { load(); }, [load]);

  const [lvl, setLvl] = useState({});
  const confirm = async (id) => {
    const rec = list.find((x) => x.id === id);
    if (rec?.source === "application" && !lvl[id]) { alert("Najprv vyberte úroveň znalostí."); return; }
    if (!window.confirm("Schváliť záznam? Vygeneruje sa podpísané PDF a záznam sa zapíše do Siene slávy (Tigik).")) return;
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/admin/mentor-audits/${id}/confirm`, lvl[id] ? { knowledge_level: lvl[id] } : {}, auth);
      alert(data.email_sent ? "Schválené a PDF odoslané." : "Schválené (e-mail sa nepodarilo odoslať).");
      load();
      onChange && onChange();
    } catch (e) { alert(e?.response?.data?.detail || "Schválenie zlyhalo."); }
    finally { setBusyId(""); }
  };

  const base = pendingOnly ? list.filter((a) => a.status === "pending") : list;
  const KIND_LBL = { verified: "Preverený", graduate: "Absolvent WLE©", training: "Absolvent Tréningového centra" };
  const hq = histQ.trim().toLowerCase();
  const history = list.filter((a) => a.status === "confirmed" && (!hq || [a.subject_name, a.card_id, a.record_no, a.area].some((f) => (f || "").toLowerCase().includes(hq))))
    .sort((x, y) => (y.confirmed_at || "").localeCompare(x.confirmed_at || ""));
  const verified = base.filter((a) => (a.kind || "verified") === "verified");
  const graduates = base.filter((a) => a.kind === "graduate");
  const training = base.filter((a) => a.kind === "training");

  const Section = ({ title, Icon, items, accent, showKind = false }) => (
    <div className="mb-10" data-testid={`records-${accent}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-[#D4AF37]" />
        <h3 className="font-display text-lg text-white">{title}</h3>
        <span className="text-white/40 text-sm">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-white/35 text-sm border border-dashed border-white/15 px-4 py-5">Zatiaľ žiadne záznamy.</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const st = STATUS[a.status] || STATUS.pending;
            return (
              <div key={a.id} data-testid={`record-row-${a.id}`} className="border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {a.record_no ? <span className="text-[#D4AF37]">{a.record_no}</span> : <span className="text-white/40">bez čísla</span>}
                    <span className="text-white/70"> · {a.subject_name}</span>
                    <span className="text-white/40 font-normal"> · Card ID {a.card_id}</span>
                    {showKind && <span className="text-white/40 font-normal"> · {KIND_LBL[a.kind || "verified"]}</span>}
                  </p>
                  {a.status === "confirmed" && (a.skills?.length > 0 || a.skills_other) && (
                    <p className="text-[#D4AF37]/70 text-xs mt-0.5" data-testid={`record-skills-${a.id}`}>Zručnosti: {(a.skills || []).length}{a.skills_other ? " + iné" : ""}</p>
                  )}
                  <p className="text-white/35 text-xs mt-0.5">
                    {a.area || "—"} · mentor: {a.mentor_name} · {(a.audit_date || "").slice(0, 10)}
                    {a.status === "confirmed" && a.tigik_ok ? <span className="text-emerald-300/70"> · v Sieni slávy</span> : null}
                    {a.resubmitted_at && a.status === "pending" ? <span className="text-amber-300/80"> · opravené mentorom</span> : null}
                  </p>
                  {a.status === "returned" && a.return_note && <p className="text-red-300/80 text-xs mt-1" data-testid={`record-return-note-${a.id}`}>Poznámka: {a.return_note}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${st.cls}`}><st.Icon size={12} /> {st.label}</span>
                  <button onClick={() => preview(a.id)} disabled={prevId === a.id} data-testid={`records-preview-${a.id}`}
                    className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#F2E3C6] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40">
                    {prevId === a.id ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Náhľad
                  </button>
                  {a.status === "pending" && a.source === "application" && (
                    <select value={lvl[a.id] || ""} onChange={(e) => setLvl((m) => ({ ...m, [a.id]: e.target.value }))} data-testid={`records-level-${a.id}`}
                      className="bg-black border border-[#D4AF37]/40 text-[#F2E3C6] px-2 py-1.5 text-xs outline-none">
                      <option value="">Úroveň znalostí…</option>
                      <option value="beginner">Začiatočník</option>
                      <option value="advanced">Pokročilý</option>
                      <option value="expert">Vysoká úroveň</option>
                    </select>
                  )}
                  {a.status === "pending" && a.mentor_id && (
                    <button onClick={() => setRet({ a, note: "" })} data-testid={`records-return-${a.id}`}
                      className="inline-flex items-center gap-1.5 border border-red-400/40 text-red-300 px-3 py-1.5 text-xs hover:bg-red-400/10 transition-colors">
                      <Undo2 size={12} /> Vrátiť na opravu
                    </button>
                  )}
                  {a.status === "pending" && (
                    <button onClick={() => confirm(a.id)} disabled={busyId === a.id} data-testid={`records-confirm-${a.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-black font-semibold px-3 py-1.5 text-xs hover:bg-[#e5c14f] transition-colors disabled:opacity-40">
                      {busyId === a.id ? <Loader2 size={12} className="animate-spin" /> : <FileCheck2 size={12} />} Schváliť
                    </button>
                  )}
                  {a.status === "pending" && (
                    <button onClick={() => removeRec(a)} disabled={busyId === a.id} data-testid={`records-delete-${a.id}`}
                      className="inline-flex items-center gap-1.5 border border-white/15 text-white/50 px-3 py-1.5 text-xs hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-40">
                      <Trash2 size={12} /> Zmazať
                    </button>
                  )}
                  {a.status === "confirmed" && (
                    <button onClick={() => setEdit(a)} data-testid={`records-edit-${a.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#F2E3C6] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors">
                      <PenLine size={12} /> Upraviť / zručnosti
                    </button>
                  )}
                  {a.status === "confirmed" && (
                    <button onClick={() => resend(a)} disabled={busyId === a.id} data-testid={`records-resend-${a.id}`}
                      className="inline-flex items-center gap-1.5 border border-white/15 text-white/70 px-3 py-1.5 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-40">
                      {busyId === a.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Poslať znova
                    </button>
                  )}
                  {a.status === "confirmed" && a.tigik_public_url && (
                    <a href={a.tigik_public_url} target="_blank" rel="noreferrer" data-testid={`records-hof-${a.id}`}
                      className="inline-flex items-center gap-1.5 border border-white/15 text-white/70 px-3 py-1.5 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                      <ExternalLink size={12} /> Sieň slávy
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="admin-records">
      {edit && (
        <ConfirmedEditModal a={edit} token={token} onClose={() => setEdit(null)}
          onSaved={(d) => { setEdit(null); load(); alert(d.tigik?.ok ? "Uložené a aktualizované v Sieni slávy." : "Uložené. Sieň slávy sa nepodarilo aktualizovať (Tigik)."); }} />
      )}
      {ret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" data-testid="return-modal" onClick={() => !retBusy && setRet(null)}>
          <div className="w-full max-w-lg bg-[#0a0a0a] border border-red-400/30 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl">Vrátiť na opravu</h3>
              <button onClick={() => setRet(null)} data-testid="return-close" className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-white/45 text-sm mb-4">{ret.a.record_no} · {ret.a.subject_name} · mentor: {ret.a.mentor_name}</p>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Čo treba opraviť (mentor dostane e-mail)</label>
            <textarea value={ret.note} onChange={(e) => setRet((r) => ({ ...r, note: e.target.value }))} rows={5} autoFocus data-testid="return-note"
              placeholder="Napr. Opravte prosím dátum ukončenia a doplňte rozsah doškolenia."
              className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setRet(null)} disabled={retBusy} className="px-4 py-2 text-sm text-white/60 hover:text-white">Zrušiť</button>
              <button onClick={sendReturn} disabled={retBusy} data-testid="return-send"
                className="inline-flex items-center gap-2 bg-red-400/90 text-black font-semibold px-5 py-2 text-sm hover:bg-red-300 transition-colors disabled:opacity-50">
                {retBusy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Vrátiť mentorovi
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList size={22} className="text-[#D4AF37]" />
        <h2 className="font-display text-2xl">{pendingOnly ? "Na podpis — potvrdenia od mentorov" : "Evidencia — Preverení a Absolventi"}</h2>
      </div>
      <p className="text-white/40 text-sm mb-6">{pendingOnly ? "Potvrdenia, ktoré mentori podpísali a čakajú na váš podpis a razítko. Pozrite si Náhľad a kliknite Schváliť — PDF sa odošle mentorovi aj absolventovi a záznam sa zapíše do Siene slávy." : "Všetky záznamy od mentorov s prideleným evidenčným číslom."} Preverení: <span className="text-[#D4AF37]/80">Sa.Eko.Verified.</span> · Absolventi Work, learn and earn©: <span className="text-[#D4AF37]/80">Sa.Eko.Con.</span> · Absolventi Tréningového centra: <span className="text-[#D4AF37]/80">Sa.Eko.Train.</span></p>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-10"><Loader2 size={16} className="animate-spin" /> Načítavam…</div>
      ) : (
        <>
          <Section title="Preverení" Icon={ShieldCheck} items={verified} accent="verified" />
          <Section title="Absolventi — Work, learn and earn©" Icon={GraduationCap} items={graduates} accent="graduates" />
          <Section title="Absolventi Tréningového centra" Icon={Building2} items={training} accent="training" />
          {pendingOnly && (
            <div className="pt-6 border-t border-white/10" data-testid="records-history">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <History size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-lg text-white">História schválených</h3>
                  <span className="text-white/40 text-sm">({history.length})</span>
                </div>
                <div className="relative sm:w-72">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                  <input value={histQ} onChange={(e) => setHistQ(e.target.value)} placeholder="Hľadať meno, Card ID, číslo…" data-testid="records-history-search"
                    className="w-full bg-black border border-white/15 pl-8 pr-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <Section title="Schválení preverení a absolventi" Icon={CheckCircle2} items={history} accent="history-list" showKind />
            </div>
          )}
        </>
      )}
    </div>
  );
}
